/**
 * Creator Dashboards — create, find and manage the persistent payout report
 * dashboards at culturegenesis.com/creators/{slug}/reports (creator and
 * agency layouts).
 *
 * Dashboards never expire: access is a 4-digit passcode the admin can view,
 * copy and reset here. The list is searchable by creator, agency (ChannelMeter
 * payee), channel name or id, slug or a pasted link, and the search and
 * filters live in the URL. Rarely used and destructive actions sit in each
 * row's ⋯ menu. The channel picker and form pieces live in ChannelPicker.tsx
 * and formParts.tsx.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button, EmptyState, Modal, PinCell, RowActionsMenu, Spinner, StatusPill, buttonStyles, tableStyles } from '../ui';
import ConfirmDialog from '../ConfirmDialog';
import ResetPinModal from '../AdOps/CampaignReports/ResetPinModal';
import { pinProblem, randomPin, slugProblem } from '../AdOps/CampaignReports/shared';
import {
  createCreatorPage,
  deleteCreatorPage,
  listCreatorPages,
  previewCreatorPage,
  resetCreatorPagePin,
  restoreCreatorPage,
  revokeCreatorPage,
  updateCreatorPage,
} from '../../services/creatorDashboardsApi';
import { DashboardNotify } from '../../hooks/useDashboardToast';
import { fmtDate } from '../Takeovers/shared';
import { ChannelPicker, PickerSelection, PrePayoutCallout } from './ChannelPicker';
import {
  PageImageField,
  PreviewStats,
  ThemeEditor,
  ThemeSwatches,
  fieldStyle,
  isHttpsUrl,
  labelStyle,
} from './formParts';
import CreatorDashboardsToolbar from './CreatorDashboardsToolbar';
import {
  DashboardRow,
  DashboardStatusFilter,
  DashboardTypeFilter,
  STATUS_FILTERS,
  TYPE_FILTERS,
  channelLabelsOf,
  filterCreatorDashboards,
  isLocked,
} from './creatorDashboardSearch';
import { CreatorPageChannelLabel, CreatorPageTheme, CreatorPageType, CreatorReportPage } from './types';

interface CreatorDashboardsTabProps {
  notify: DashboardNotify;
  /** The create modal is opened from the dashboard's top bar. */
  showCreate: boolean;
  onShowCreateChange: (open: boolean) => void;
}

/** Mirrors the backend caps (creatorPagesFirestore.ts). */
const MAX_CHANNELS_BY_TYPE: Record<CreatorPageType, number> = { creator: 10, agency: 30 };
const PUBLIC_PAGES_BASE = 'https://www.culturegenesis.com';
/** Creator-only reserved slug: the public sample dashboard (mirrors CREATOR_RESERVED_SLUGS). */
const DEMO_SLUG = 'demo-report';
/** The server answered with stored channel names only — ask again once, a little later. */
const PARTIAL_LABELS_RETRY_MS = 5000;

const pageUrl = (slug: string) => `${PUBLIC_PAGES_BASE}/creators/${slug}/reports`;
const shareText = (url: string, pin: string) => `${url}\nPasscode: ${pin}`;

/** Mirror of the backend slugify (takeoversFirestore.ts) for live URL preview. */
const localSlugify = (name: string): string =>
  name
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/['’]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);

/** "Oct 15, 2:14 PM" — the year only when it isn't this year. */
const fmtShortDateTime = (iso: string): string => {
  const d = new Date(iso);
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
    hour: 'numeric',
    minute: '2-digit',
  });
};

const fmtLockedUntil = (iso: string): string => {
  const d = new Date(iso);
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return d.toDateString() === new Date().toDateString()
    ? time
    : `${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}, ${time}`;
};

/** "tiffany@culturegenesis.com" → "tiffany"; other domains stay whole. */
const shortEmail = (email: string): string => email.replace(/@culturegenesis\.com$/i, '');

/** "Last download Oct 18, 3:02 PM · PDF" for the download count's tooltip. */
const lastDownloadTitle = (p: CreatorReportPage): string | undefined =>
  p.lastDownloadAt
    ? `Last download ${fmtShortDateTime(p.lastDownloadAt)}${p.lastDownloadFormat ? ` · ${p.lastDownloadFormat.toUpperCase()}` : ''}`
    : undefined;

const subLineStyle: React.CSSProperties = { fontSize: 11.5, color: 'var(--fg-4)', fontWeight: 400 };

const visuallyHidden: React.CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

const fieldHint = (problem: boolean): React.CSSProperties => ({
  fontSize: 11.5,
  color: problem ? 'var(--danger)' : 'var(--fg-4)',
  marginTop: 4,
});

/** Channel names (first two, then "+N more"); the full list with payees is in the tooltip. */
const ChannelsCell: React.FC<{ page: CreatorReportPage; matched?: CreatorPageChannelLabel }> = ({ page, matched }) => {
  const labels = channelLabelsOf(page);
  const named = labels.filter((l) => l.channelName !== l.channelId);
  const shown = named.slice(0, 2).map((l) => l.channelName);
  const more = labels.length - shown.length;
  const title = labels.map((l) => (l.payeeName ? `${l.channelName} — ${l.payeeName}` : l.channelName)).join('\n');
  return (
    <div title={title} style={{ minWidth: 140, maxWidth: 220 }}>
      {shown.length > 0 ? (
        <>
          {shown.join(', ')}
          {more > 0 && <span style={{ color: 'var(--fg-4)' }}> +{more} more</span>}
        </>
      ) : (
        `${labels.length} channel${labels.length === 1 ? '' : 's'}`
      )}
      {matched && (
        <div style={{ ...subLineStyle, color: 'var(--accent-soft-fg)', marginTop: 3 }}>
          matched: {matched.channelName}
          {matched.payeeName ? ` · ${matched.payeeName}` : ''}
        </div>
      )}
    </div>
  );
};

const CreatorDashboardsTab: React.FC<CreatorDashboardsTabProps> = ({ notify, showCreate, onShowCreateChange }) => {
  const [pages, setPages] = useState<CreatorReportPage[]>([]);
  const [loading, setLoading] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [labelsPartial, setLabelsPartial] = useState(false);
  const [banner, setBanner] = useState<{ title: string; url: string; pin: string } | null>(null);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const listSeq = useRef(0);
  const loadedRef = useRef(false);
  const partialRetried = useRef(false);

  // Search + filters live in the URL (shareable, survive a refresh). The text
  // box keeps its own state so typing never waits on a router transition.
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState(() => searchParams.get('q') ?? '');
  const typeParam = searchParams.get('type') as DashboardTypeFilter | null;
  const statusParam = searchParams.get('status') as DashboardStatusFilter | null;
  const typeFilter: DashboardTypeFilter = typeParam && TYPE_FILTERS.includes(typeParam) ? typeParam : 'all';
  const statusFilter: DashboardStatusFilter = statusParam && STATUS_FILTERS.includes(statusParam) ? statusParam : 'any';

  const setParams = useCallback(
    (changes: Partial<Record<'q' | 'type' | 'status', string | null>>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          (Object.keys(changes) as Array<'q' | 'type' | 'status'>).forEach((key) => {
            const value = changes[key];
            if (value) next.set(key, value);
            else next.delete(key);
          });
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );
  const changeQuery = (q: string) => {
    setQuery(q);
    setParams({ q: q.trim() ? q : null });
  };
  const changeType = (t: DashboardTypeFilter) => setParams({ type: t === 'all' ? null : t });
  const changeStatus = (s: DashboardStatusFilter) => setParams({ status: s === 'any' ? null : s });
  const clearFilters = useCallback(() => {
    setQuery('');
    setParams({ q: null, type: null, status: null });
  }, [setParams]);

  // Create-modal state (kept while the modal is closed, so Cancel doesn't lose a draft)
  const [creating, setCreating] = useState(false);
  const [pageType, setPageType] = useState<CreatorPageType>('creator');
  const [clientLabel, setClientLabel] = useState('');
  const [slugInput, setSlugInput] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [pin, setPin] = useState<string>(() => randomPin());
  const [includeAdvertisers, setIncludeAdvertisers] = useState(true);
  const [includeAdSense, setIncludeAdSense] = useState(true);
  const [allowDownloads, setAllowDownloads] = useState(true);
  const [avatarOverride, setAvatarOverride] = useState('');
  const [selected, setSelected] = useState<PickerSelection>(new Map());
  const [preview, setPreview] = useState<{ key: string; report: any } | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [themeDraft, setThemeDraft] = useState<CreatorPageTheme | null>(null);
  const [themeTouched, setThemeTouched] = useState(false);

  // Edit-modal state. editSeq changes whenever the modal opens or closes, so a
  // preview or re-extract that lands late can't touch another dashboard.
  const [editTarget, setEditTarget] = useState<CreatorReportPage | null>(null);
  const [editLabel, setEditLabel] = useState('');
  const [editAdvertisers, setEditAdvertisers] = useState(true);
  const [editAdSense, setEditAdSense] = useState(true);
  const [editAllowDownloads, setEditAllowDownloads] = useState(true);
  const [editAvatarUrl, setEditAvatarUrl] = useState('');
  const [editSelected, setEditSelected] = useState<PickerSelection>(new Map());
  const [editPreview, setEditPreview] = useState<{ key: string; report: any } | null>(null);
  const [editPreviewing, setEditPreviewing] = useState(false);
  const [editTheme, setEditTheme] = useState<CreatorPageTheme | null>(null);
  const [editThemeTouched, setEditThemeTouched] = useState(false);
  const [editExtracting, setEditExtracting] = useState(false);
  const [saving, setSaving] = useState(false);
  const editSeq = useRef(0);

  const [resetTarget, setResetTarget] = useState<CreatorReportPage | null>(null);
  const [deactivateTarget, setDeactivateTarget] = useState<CreatorReportPage | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<CreatorReportPage | null>(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  /**
   * Reload the list without blanking it: the table stays up while a refresh
   * runs. Only the newest request may set state. A failure before anything
   * loaded shows an error card; after that it's a toast over the last list.
   */
  const refresh = useCallback(() => {
    const seq = ++listSeq.current;
    setLoading(true);
    listCreatorPages()
      .then((r) => {
        if (seq !== listSeq.current) return;
        setPages(r.pages);
        setLabelsPartial(r.labelsPartial);
        setLoadError(null);
        setLoaded(true);
        loadedRef.current = true;
      })
      .catch((err) => {
        if (seq !== listSeq.current) return;
        const message = err?.message || 'Unknown error';
        if (loadedRef.current) notify('error', 'Couldn’t refresh creator dashboards', message);
        else setLoadError(message);
      })
      .finally(() => {
        if (seq === listSeq.current) setLoading(false);
      });
  }, [notify]);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    if (!labelsPartial || partialRetried.current) return;
    partialRetried.current = true;
    const t = window.setTimeout(refresh, PARTIAL_LABELS_RETRY_MS);
    return () => window.clearTimeout(t);
  }, [labelsPartial, refresh]);

  const { rows, counts } = useMemo(
    () => filterCreatorDashboards(pages, { q: query, type: typeFilter, status: statusFilter }),
    [pages, query, typeFilter, statusFilter],
  );

  const effectiveSlug = useMemo(
    () => (slugTouched ? localSlugify(slugInput) : localSlugify(clientLabel)),
    [slugTouched, slugInput, clientLabel],
  );

  /** What the server would refuse about the slug, checked as the admin types. */
  const createSlugProblem = useMemo((): string | null => {
    if (!effectiveSlug) return 'Type a URL slug — the label has no letters or numbers to build one from.';
    const generic = slugProblem(effectiveSlug);
    if (generic) return generic;
    if (effectiveSlug === DEMO_SLUG) return `“${DEMO_SLUG}” is the public sample dashboard — pick a different slug`;
    const taken = pages.find((p) => p.slug === effectiveSlug);
    if (taken) return `“${effectiveSlug}” is already used by ${taken.clientLabel} — try “${effectiveSlug}-2”`;
    return null;
  }, [effectiveSlug, pages]);
  const shownSlugProblem = clientLabel.trim() || slugTouched ? createSlugProblem : null;
  const pinIssue = pin.length === 4 ? pinProblem(pin) : null;

  const maxChannels = MAX_CHANNELS_BY_TYPE[pageType];

  /**
   * What a create preview was computed for. A preview for another selection,
   * page type or toggle set hides itself, so its avatar/banner can never be
   * saved with the wrong channels. Allow downloads is left out: it changes
   * nothing a preview shows, and the preview route refuses the field.
   */
  const createPreviewKey = [Array.from(selected.keys()).sort().join(','), pageType, includeAdvertisers, includeAdSense].join('|');
  const currentPreview = preview?.key === createPreviewKey ? preview.report : null;

  /** Advertiser strips suit creator dashboards; agency dashboards default to the lean roster view. */
  const switchPageType = (next: CreatorPageType) => {
    if (next === pageType) return;
    setPageType(next);
    setIncludeAdvertisers(next === 'creator');
    // The preview's avatar/banner/theme were resolved with the previous type's
    // channel ordering (agency ranks by subscribers) and `mint` persists them —
    // drop it so the admin re-previews instead of shipping the wrong default image.
    setPreview(null);
    if (!themeTouched) setThemeDraft(null);
  };

  const copyText = async (key: string, text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey((k) => (k === key ? null : k)), 1600);
    } catch {
      notify('error', 'Copy failed', 'Your browser blocked clipboard access — select the text and copy it instead.');
    }
  };

  const resetCreate = () => {
    setPageType('creator');
    setClientLabel('');
    setSlugInput('');
    setSlugTouched(false);
    setPin(randomPin());
    setSelected(new Map());
    setPreview(null);
    setThemeDraft(null);
    setThemeTouched(false);
    setIncludeAdvertisers(true);
    setIncludeAdSense(true);
    setAllowDownloads(true);
    setAvatarOverride('');
  };

  const capLabel = () => `${pageType === 'agency' ? 'An agency' : 'A creator'} dashboard supports at most ${maxChannels} channels`;

  const avatarOverrideValid = avatarOverride.trim() === '' || isHttpsUrl(avatarOverride);
  /** The image that will be persisted: admin override wins, else the preview's resolved art. */
  const effectiveAvatarUrl = isHttpsUrl(avatarOverride) ? avatarOverride.trim() : currentPreview?.avatarUrl || undefined;

  const runPreview = async () => {
    if (selected.size === 0) return;
    const key = createPreviewKey;
    setPreviewing(true);
    try {
      const report = await previewCreatorPage({
        channelIds: Array.from(selected.keys()),
        pageType,
        includeAdvertisers,
        includeAdSense,
        clientLabel: clientLabel || undefined,
        avatarUrl: isHttpsUrl(avatarOverride) ? avatarOverride.trim() : undefined,
      });
      setPreview({ key, report });
      if (!themeTouched) setThemeDraft(report.theme);
    } catch (err: any) {
      notify('error', 'Preview failed', err?.message);
    } finally {
      setPreviewing(false);
    }
  };

  const mint = async () => {
    if (!clientLabel.trim() || selected.size === 0) {
      notify('error', 'Pick at least one channel and a client label');
      return;
    }
    if (selected.size > maxChannels) {
      notify('error', capLabel(), 'Remove some channels or switch the dashboard type.');
      return;
    }
    if (createSlugProblem) {
      notify('error', 'Fix the dashboard URL', createSlugProblem);
      return;
    }
    const pinError = pinProblem(pin);
    if (pinError) {
      notify('error', pinError);
      return;
    }
    if (!avatarOverrideValid) {
      notify('error', 'Dashboard image must be a full https:// URL');
      return;
    }
    setCreating(true);
    try {
      const { page, url } = await createCreatorPage({
        channelIds: Array.from(selected.keys()),
        clientLabel: clientLabel.trim(),
        pageType,
        slug: effectiveSlug,
        pin,
        includeAdvertisers,
        includeAdSense,
        // Absent = allowed, so only an opt-out goes on the wire.
        allowDownloads: allowDownloads ? undefined : false,
        theme: themeTouched && themeDraft ? themeDraft : undefined,
        avatarUrl: effectiveAvatarUrl,
        bannerUrl: currentPreview?.bannerUrl || undefined,
      });
      setBanner({ title: `Creator dashboard created for ${page.clientLabel} — share the link and passcode.`, url, pin: page.pin });
      onShowCreateChange(false);
      resetCreate();
      clearFilters(); // so the new row is visible
      refresh();
    } catch (err: any) {
      notify('error', err?.status === 409 ? 'Slug already in use' : 'Failed to create dashboard', err?.message);
    } finally {
      setCreating(false);
    }
  };

  const editMaxChannels = MAX_CHANNELS_BY_TYPE[editTarget?.pageType ?? 'creator'];
  const editCapLabel = `${editTarget?.pageType === 'agency' ? 'An agency' : 'A creator'} dashboard supports at most ${editMaxChannels} channels`;

  const openEdit = (p: CreatorReportPage) => {
    editSeq.current += 1;
    setEditTarget(p);
    setEditLabel(p.clientLabel);
    setEditAdvertisers(p.includeAdvertisers);
    setEditAdSense(p.includeAdSense !== false); // legacy pages predate the field
    setEditAllowDownloads(p.allowDownloads !== false); // absent on pages made before downloads
    setEditAvatarUrl(p.avatarUrl || '');
    setEditTheme(p.theme);
    setEditThemeTouched(false);
    setEditPreview(null);
    setEditPreviewing(false);
    setEditExtracting(false);
    // Seed pills straight away with the best names we have, flagged pending;
    // ChannelPicker refreshes them in one batched lookup. Anything it can't
    // resolve keeps its seeded label — still removable and saveable.
    const names = new Map(channelLabelsOf(p).map((l) => [l.channelId, l.channelName]));
    setEditSelected(new Map(p.channelIds.map((id) => [
      id,
      {
        channelId: id,
        channelName: names.get(id) || p.channelNames?.[id] || id,
        firstMonth: '',
        lastMonth: '',
        monthCount: 0,
        hasDirectSold: false,
        ...(p.firstPayoutPending?.[id] ? { hasPayouts: false } : {}),
        pending: true,
      },
    ])));
  };

  const closeEdit = () => {
    editSeq.current += 1;
    setEditTarget(null);
  };

  /** What an edit preview was computed for — channels and the toggles that change its totals. */
  const editPreviewKey = [Array.from(editSelected.keys()).sort().join(','), editAdvertisers, editAdSense].join('|');
  const editLabelProblem = editTarget && !editLabel.trim() ? 'The client label can’t be empty.' : null;

  const runEditPreview = async () => {
    if (!editTarget || editSelected.size === 0) return;
    const seq = editSeq.current;
    const key = editPreviewKey;
    setEditPreviewing(true);
    try {
      const report = await previewCreatorPage({
        channelIds: Array.from(editSelected.keys()),
        pageType: editTarget.pageType,
        includeAdvertisers: editAdvertisers,
        includeAdSense: editAdSense,
        clientLabel: editLabel || undefined,
      });
      if (seq === editSeq.current) setEditPreview({ key, report });
    } catch (err: any) {
      if (seq === editSeq.current) notify('error', 'Preview failed', err?.message);
    } finally {
      if (seq === editSeq.current) setEditPreviewing(false);
    }
  };

  const reExtractTheme = async () => {
    if (!editTarget) return;
    if (editSelected.size === 0) {
      notify('error', 'Pick at least one channel to extract a theme from');
      return;
    }
    const seq = editSeq.current;
    setEditExtracting(true);
    try {
      const report = await previewCreatorPage({
        channelIds: Array.from(editSelected.keys()),
        pageType: editTarget.pageType,
        includeAdvertisers: false,
      });
      if (seq !== editSeq.current) return;
      setEditTheme(report.theme);
      setEditThemeTouched(true);
      notify('success', `Theme re-extracted from channel ${report.themeSource === 'banner' ? 'banner' : 'art'}`);
    } catch (err: any) {
      if (seq === editSeq.current) notify('error', 'Extraction failed', err?.message);
    } finally {
      if (seq === editSeq.current) setEditExtracting(false);
    }
  };

  const saveEdit = async () => {
    if (!editTarget) return;
    if (editLabelProblem) {
      notify('error', editLabelProblem);
      return;
    }
    const patch: Record<string, unknown> = {};
    if (editLabel.trim() !== editTarget.clientLabel) patch.clientLabel = editLabel.trim();
    if (editAdvertisers !== editTarget.includeAdvertisers) patch.includeAdvertisers = editAdvertisers;
    if (editAdSense !== (editTarget.includeAdSense !== false)) patch.includeAdSense = editAdSense;
    if (editAllowDownloads !== (editTarget.allowDownloads !== false)) patch.allowDownloads = editAllowDownloads;
    if (editThemeTouched && editTheme) patch.theme = editTheme;
    const trimmedAvatar = editAvatarUrl.trim();
    if (trimmedAvatar !== (editTarget.avatarUrl || '')) {
      // '' is meaningful: it clears the stored image server-side.
      if (trimmedAvatar && !isHttpsUrl(trimmedAvatar)) {
        notify('error', 'Dashboard image must be a full https:// URL');
        return;
      }
      patch.avatarUrl = trimmedAvatar;
    }
    const newChannelIds = Array.from(editSelected.keys());
    if (newChannelIds.length === 0) {
      notify('error', 'At least one channel is required');
      return;
    }
    // Order-insensitive diff: reordering alone isn't a change worth a PATCH.
    if ([...newChannelIds].sort().join(',') !== [...editTarget.channelIds].sort().join(',')) {
      patch.channelIds = newChannelIds;
    }
    if (Object.keys(patch).length === 0) { closeEdit(); return; }
    setSaving(true);
    try {
      await updateCreatorPage(editTarget.slug, patch);
      notify('success', 'Dashboard updated');
      closeEdit();
      refresh();
    } catch (err: any) {
      notify('error', 'Update failed', err?.message);
    } finally {
      setSaving(false);
    }
  };

  /** ResetPinModal handles its own busy state; errors are reported here. */
  const runResetPin = async (newPin: string | undefined) => {
    if (!resetTarget) return;
    try {
      const page = await resetCreatorPagePin(resetTarget.slug, newPin);
      // The PIN goes in the banner (copyable, stays up), never in a 5-second toast.
      setBanner({
        title: `New PIN for ${page.clientLabel} — the old one stopped working on every device.`,
        url: pageUrl(page.slug),
        pin: page.pin,
      });
      notify('success', 'PIN reset', 'Copy the new PIN from the banner at the top of the list.');
      setResetTarget(null);
      refresh();
    } catch (err: any) {
      notify('error', 'PIN reset failed', err?.message);
    }
  };

  const runDelete = async () => {
    if (!deleteTarget || deleteConfirmText !== deleteTarget.slug) return;
    setDeleting(true);
    try {
      await deleteCreatorPage(deleteTarget.slug);
      notify('success', 'Dashboard deleted', `${deleteTarget.clientLabel} is gone — the URL now shows “not found.”`);
      setDeleteTarget(null);
      setDeleteConfirmText('');
      refresh();
    } catch (err: any) {
      notify('error', 'Delete failed', err?.message);
    } finally {
      setDeleting(false);
    }
  };

  const runDeactivate = async () => {
    const target = deactivateTarget;
    if (!target) return;
    setDeactivateTarget(null);
    try {
      await revokeCreatorPage(target.slug);
      notify('success', 'Dashboard deactivated', `${target.clientLabel} now shows “deactivated” until you restore it.`);
      refresh();
    } catch (err: any) {
      notify('error', 'Deactivate failed', err?.message);
    }
  };

  const restore = async (p: CreatorReportPage) => {
    try {
      await restoreCreatorPage(p.slug);
      notify('success', 'Dashboard restored', 'Same link and passcode — every device has to enter the passcode again.');
      refresh();
    } catch (err: any) {
      notify('error', 'Restore failed', err?.message);
    }
  };

  const renderRow = ({ page: p, matchedChannel }: DashboardRow) => (
    <tr key={p.slug}>
      <td style={{ ...tableStyles.td, minWidth: 200, maxWidth: 280 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600 }}>
          <span>{p.clientLabel}</span>
          {p.pageType === 'agency' && <StatusPill variant="approved" size="sm" noDot>Agency</StatusPill>}
        </div>
        <div style={{ ...subLineStyle, marginTop: 3, wordBreak: 'break-word' }}>/creators/{p.slug}/reports</div>
        <div style={{ ...subLineStyle, display: 'flex', alignItems: 'center', gap: 8, marginTop: 4, flexWrap: 'wrap' }}>
          <ThemeSwatches theme={p.theme} />
          <span title={p.createdBy}>Created {fmtDate(p.createdAt)} · {shortEmail(p.createdBy)}</span>
        </div>
      </td>
      <td style={tableStyles.td}>
        <ChannelsCell page={p} matched={matchedChannel} />
      </td>
      <td style={tableStyles.td}>
        <PinCell
          label={p.clientLabel}
          pin={p.pin}
          copied={copiedKey === `pin:${p.slug}`}
          onCopy={() => copyText(`pin:${p.slug}`, p.pin)}
          onReset={() => setResetTarget(p)}
        />
      </td>
      <td style={{ ...tableStyles.td, whiteSpace: 'nowrap' }}>
        {/* Breaks after the dot when space is short, so the download count never widens the table. */}
        <div style={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'normal' }}>
          <span style={{ whiteSpace: 'nowrap' }}>{p.accessCount} unlock{p.accessCount === 1 ? '' : 's'}</span>
          {(p.downloadCount ?? 0) > 0 && (
            <>
              {' · '}
              <span title={lastDownloadTitle(p)} style={{ whiteSpace: 'nowrap' }}>
                {p.downloadCount} download{p.downloadCount === 1 ? '' : 's'}
              </span>
            </>
          )}
        </div>
        <div style={{ ...subLineStyle, marginTop: 3 }}>{p.lastAccessAt ? `Last ${fmtShortDateTime(p.lastAccessAt)}` : 'Not opened yet'}</div>
        {p.allowDownloads === false && <div style={{ ...subLineStyle, marginTop: 3 }}>Downloads off</div>}
      </td>
      <td style={tableStyles.td}>
        <StatusPill variant={p.revokedAt ? 'paused' : 'ok'}>{p.revokedAt ? 'Deactivated' : 'Active'}</StatusPill>
        {isLocked(p) && p.lockedUntil && (
          <div style={{ fontSize: 11.5, color: 'var(--warning)', marginTop: 4, maxWidth: 170 }}>
            <strong>Locked</strong> until {fmtLockedUntil(p.lockedUntil)} · Reset PIN clears it
          </div>
        )}
      </td>
      <td style={{ ...tableStyles.td, whiteSpace: 'nowrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 6 }}>
          <a
            href={pageUrl(p.slug)}
            target="_blank"
            rel="noreferrer"
            style={buttonStyles('subtle', 'sm')}
            aria-label={`Open ${p.clientLabel} (new tab)`}
          >
            Open ↗
          </a>
          <Button size="sm" variant="ghost" onClick={() => copyText(`link:${p.slug}`, shareText(pageUrl(p.slug), p.pin))}>
            {copiedKey === `link:${p.slug}` ? 'Copied ✓' : 'Copy link + PIN'}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => openEdit(p)} aria-label={`Edit ${p.clientLabel}`}>
            Edit
          </Button>
          <RowActionsMenu
            label={p.clientLabel}
            actions={[
              p.revokedAt
                ? { label: 'Restore', onSelect: () => restore(p) }
                : { label: 'Deactivate…', onSelect: () => setDeactivateTarget(p) },
              { label: 'Delete…', danger: true, onSelect: () => { setDeleteTarget(p); setDeleteConfirmText(''); } },
            ]}
          />
        </div>
      </td>
    </tr>
  );

  const renderList = () => {
    if (!loaded) {
      if (loadError) {
        return (
          <div
            role="alert"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
              flexWrap: 'wrap',
              padding: '14px 16px',
              borderRadius: 'var(--radius-lg)',
              background: 'var(--danger-soft)',
              border: '1px solid var(--border-1)',
              fontSize: 13,
            }}
          >
            <div>
              <strong>Couldn’t load creator dashboards.</strong>{' '}
              <span style={{ color: 'var(--fg-3)' }}>{loadError}</span>
            </div>
            <Button size="sm" variant="ghost" onClick={refresh} disabled={loading}>
              {loading ? 'Retrying…' : 'Retry'}
            </Button>
          </div>
        );
      }
      return <div style={{ display: 'flex', justifyContent: 'center', padding: 50 }}><Spinner /></div>;
    }
    if (pages.length === 0) {
      return <EmptyState title="No creator dashboards yet" subtitle="Create one to share a payout report with a creator or agency." />;
    }
    return (
      <>
        <CreatorDashboardsToolbar
          query={query}
          onQueryChange={changeQuery}
          type={typeFilter}
          onTypeChange={changeType}
          status={statusFilter}
          onStatusChange={changeStatus}
          counts={counts}
          shown={rows.length}
          total={pages.length}
          refreshing={loading}
          onClearAll={clearFilters}
        />
        {rows.length === 0 ? (
          <div
            style={{
              padding: '28px 16px',
              textAlign: 'center',
              border: '1px dashed var(--border-1)',
              borderRadius: 'var(--radius-lg)',
              color: 'var(--fg-3)',
              fontSize: 13,
            }}
          >
            No creator dashboards match {query.trim() ? `“${query.trim()}”` : 'these filters'}.{' '}
            <Button size="sm" variant="ghost" onClick={clearFilters} style={{ marginLeft: 6 }}>
              Clear filters
            </Button>
          </div>
        ) : (
          <div style={tableStyles.wrapScroll}>
            <table style={tableStyles.table}>
              <thead>
                <tr>
                  <th style={tableStyles.th}>Creator / Agency</th>
                  <th style={tableStyles.th}>Channels</th>
                  <th style={tableStyles.th}>PIN</th>
                  <th
                    style={tableStyles.th}
                    title="Unlocks are passcode entries — return visits from a remembered device aren’t counted. Downloads are spreadsheets and PDFs the client saved from their report page."
                  >
                    Activity
                  </th>
                  <th style={tableStyles.th}>Status</th>
                  <th style={tableStyles.th}><span style={visuallyHidden}>Actions</span></th>
                </tr>
              </thead>
              <tbody>{rows.map(renderRow)}</tbody>
            </table>
          </div>
        )}
      </>
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ fontSize: 13, color: 'var(--fg-3)' }}>
        Passcode-protected payout dashboards for creators and agencies. Links never expire — deactivate or delete any time.
      </div>

      {banner && (
        <div
          role="status"
          style={{
            background: 'var(--success-soft)',
            border: '1px solid var(--success)',
            borderRadius: 'var(--radius-md)',
            padding: '12px 14px',
          }}
        >
          <div style={{ fontWeight: 700, fontSize: 13.5, marginBottom: 8 }}>{banner.title}</div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <code style={{ fontSize: 12, flex: 1, minWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{banner.url}</code>
            <code style={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.12em' }}>PIN {banner.pin}</code>
            <Button size="sm" variant="primary" onClick={() => copyText('banner', shareText(banner.url, banner.pin))}>
              {copiedKey === 'banner' ? 'Copied ✓' : 'Copy link + PIN'}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setBanner(null)}>Dismiss</Button>
          </div>
        </div>
      )}

      {renderList()}

      {showCreate && (
        <Modal
          open
          onClose={() => onShowCreateChange(false)}
          eyebrow="Creator dashboards"
          title="New creator dashboard"
          ariaLabel="New creator dashboard"
          width={680}
          closeOnEsc={false}
          closeOnBackdrop={false}
          footer={
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Button variant="ghost" onClick={() => onShowCreateChange(false)}>Cancel</Button>
              <Button variant="subtle" onClick={runPreview} disabled={previewing || selected.size === 0}>
                {previewing ? 'Previewing…' : 'Preview data & theme'}
              </Button>
              <Button variant="primary" onClick={mint} disabled={creating}>
                {creating ? 'Creating…' : 'Create dashboard'}
              </Button>
            </div>
          }
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <span style={labelStyle}>Dashboard type</span>
              <div role="group" aria-label="Dashboard type" style={{ display: 'inline-flex', border: '1px solid var(--border-1)', borderRadius: 999, overflow: 'hidden' }}>
                {(['creator', 'agency'] as CreatorPageType[]).map((t) => (
                  <button
                    key={t}
                    type="button"
                    aria-pressed={pageType === t}
                    onClick={() => switchPageType(t)}
                    style={{
                      border: 'none',
                      cursor: 'pointer',
                      padding: '8px 16px',
                      fontSize: 12.5,
                      fontWeight: 700,
                      background: pageType === t ? 'var(--accent)' : 'transparent',
                      color: pageType === t ? 'var(--fg-on-accent)' : 'var(--fg-3)',
                    }}
                  >
                    {t === 'creator' ? `Creator (≤${MAX_CHANNELS_BY_TYPE.creator} channels)` : `Agency (≤${MAX_CHANNELS_BY_TYPE.agency} channels)`}
                  </button>
                ))}
              </div>
              <div style={fieldHint(false)}>
                {pageType === 'agency'
                  ? 'High-level roster view: per-channel Direct Sold / AdSense totals with eCPM comparisons, built for agencies managing many channels.'
                  : 'The classic creator payout report with month-by-month detail.'}
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div>
                <label style={labelStyle} htmlFor="cd-create-label">Client label *</label>
                <input
                  id="cd-create-label"
                  style={fieldStyle}
                  value={clientLabel}
                  maxLength={120}
                  onChange={(e) => setClientLabel(e.target.value)}
                  placeholder={pageType === 'agency' ? 'Wildcat Management' : 'Kai Cenat'}
                />
              </div>
              <div>
                <label style={labelStyle} htmlFor="cd-create-pin">Passcode (4 digits) *</label>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input
                    id="cd-create-pin"
                    style={{ ...fieldStyle, letterSpacing: 4, fontFamily: 'var(--font-mono)', width: 96 }}
                    value={pin}
                    maxLength={4}
                    inputMode="numeric"
                    aria-invalid={!!pinIssue}
                    onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                  />
                  <Button size="sm" variant="ghost" onClick={() => setPin(randomPin())}>New random PIN</Button>
                </div>
                {pinIssue && <div style={fieldHint(true)}>{pinIssue}</div>}
              </div>
            </div>

            <div>
              <label style={labelStyle} htmlFor="cd-create-slug">Dashboard URL</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 12.5, color: 'var(--fg-4)', whiteSpace: 'nowrap' }}>culturegenesis.com/creators/</span>
                <input
                  id="cd-create-slug"
                  style={{ ...fieldStyle, width: 220 }}
                  value={slugTouched ? slugInput : effectiveSlug}
                  onChange={(e) => { setSlugTouched(true); setSlugInput(e.target.value); }}
                  placeholder="kai-cenat"
                  aria-invalid={!!shownSlugProblem}
                />
                <span style={{ fontSize: 12.5, color: 'var(--fg-4)' }}>/reports</span>
              </div>
              <div style={fieldHint(!!shownSlugProblem)}>
                {shownSlugProblem ?? (
                  <>
                    {slugTouched && effectiveSlug !== slugInput.trim() && <>Will be saved as <strong>{effectiveSlug}</strong>. </>}
                    Permanent — pick carefully. The URL can’t be changed after creation.
                  </>
                )}
              </div>
            </div>

            <div>
              <label style={{ ...labelStyle, color: selected.size > maxChannels ? 'var(--danger)' : labelStyle.color }}>
                Channels * ({selected.size}/{maxChannels})
              </label>
              <ChannelPicker
                selected={selected}
                onChange={setSelected}
                maxChannels={maxChannels}
                capMessage={capLabel()}
                notify={notify}
              />
              <PrePayoutCallout selected={selected} />
            </div>

            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
              <input type="checkbox" checked={includeAdvertisers} onChange={(e) => setIncludeAdvertisers(e.target.checked)} />
              Show advertisers CG brought to the channels
            </label>

            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
              <input type="checkbox" checked={includeAdSense} onChange={(e) => setIncludeAdSense(e.target.checked)} />
              Show AdSense payout data
              {!includeAdSense && pageType === 'agency' && (
                <span style={{ fontSize: 11.5, color: 'var(--fg-4)' }}>— eCPM comparisons stay visible per channel</span>
              )}
            </label>

            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
              <input type="checkbox" checked={allowDownloads} onChange={(e) => setAllowDownloads(e.target.checked)} />
              Allow downloads (spreadsheet and PDF)
            </label>

            <div>
              <label style={labelStyle}>Dashboard image (optional)</label>
              <PageImageField
                value={avatarOverride}
                onChange={setAvatarOverride}
                onError={(title, message) => notify('error', title, message)}
                fallbackUrl={currentPreview?.avatarUrl}
                placeholder={pageType === 'agency' ? 'https://… (agency logo)' : 'https://… (custom image)'}
                helper={
                  <div style={fieldHint(!avatarOverrideValid)}>
                    {avatarOverrideValid
                      ? pageType === 'agency'
                        ? 'Upload an agency logo, or leave blank for the default: the image of the roster’s biggest channel (most subscribers). Run Preview to see it.'
                        : 'Upload a custom image, or leave blank for the default channel art. Run Preview to see it.'
                      : 'Must be a full https:// URL.'}
                  </div>
                }
              />
            </div>

            {currentPreview && (
              <div style={{ background: 'var(--bg-surface-2)', borderRadius: 'var(--radius-md)', padding: '12px 14px', fontSize: 13, display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  {currentPreview.avatarUrl && (
                    <img src={currentPreview.avatarUrl} alt="" style={{ width: 28, height: 28, borderRadius: '50%' }} />
                  )}
                  {currentPreview.bannerUrl && (
                    <img src={currentPreview.bannerUrl} alt="" style={{ width: 84, height: 28, borderRadius: 6, objectFit: 'cover' }} />
                  )}
                  <span style={{ fontSize: 11.5, color: 'var(--fg-4)' }}>
                    Theme from {currentPreview.themeSource === 'banner' ? 'banner' : currentPreview.themeSource === 'avatar' ? 'avatar' : currentPreview.themeSource === 'manual' ? 'manual override' : 'CG fallback'}
                  </span>
                </div>
                <PreviewStats preview={currentPreview} includeAdSense={includeAdSense} />
                {themeDraft && (
                  <ThemeEditor
                    theme={themeDraft}
                    onChange={(t) => { setThemeDraft(t); setThemeTouched(true); }}
                    onReset={() => { setThemeDraft(currentPreview.theme); setThemeTouched(false); }}
                  />
                )}
              </div>
            )}
          </div>
        </Modal>
      )}

      {editTarget && (
        <Modal
          open
          onClose={closeEdit}
          eyebrow="Creator dashboards"
          title={`Edit — ${editTarget.clientLabel}`}
          ariaLabel={`Edit ${editTarget.clientLabel}`}
          width={620}
          closeOnEsc={false}
          closeOnBackdrop={false}
          footer={
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Button variant="ghost" onClick={closeEdit}>Cancel</Button>
              <Button variant="subtle" onClick={runEditPreview} disabled={editPreviewing || editSelected.size === 0}>
                {editPreviewing ? 'Previewing…' : 'Preview data'}
              </Button>
              <Button variant="primary" onClick={saveEdit} disabled={saving}>{saving ? 'Saving…' : 'Save changes'}</Button>
            </div>
          }
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ fontSize: 12.5, color: 'var(--fg-4)' }}>
              /creators/{editTarget.slug}/reports · {editTarget.pageType === 'agency' ? 'Agency dashboard' : 'Creator dashboard'} — the URL is permanent; reset the PIN from the list.
            </div>
            <div>
              <label style={labelStyle} htmlFor="cd-edit-label">Client label</label>
              <input
                id="cd-edit-label"
                style={fieldStyle}
                value={editLabel}
                maxLength={120}
                aria-invalid={!!editLabelProblem}
                onChange={(e) => setEditLabel(e.target.value)}
              />
              {editLabelProblem && <div style={fieldHint(true)}>{editLabelProblem}</div>}
            </div>
            <div>
              <label style={{ ...labelStyle, color: editSelected.size > editMaxChannels ? 'var(--danger)' : labelStyle.color }}>
                Channels ({editSelected.size}/{editMaxChannels})
              </label>
              <ChannelPicker
                selected={editSelected}
                onChange={setEditSelected}
                maxChannels={editMaxChannels}
                capMessage={editCapLabel}
                notify={notify}
              />
              <PrePayoutCallout selected={editSelected} />
              <div style={fieldHint(false)}>
                Changes apply on save — the public dashboard picks up the new channel set on its next load.
                Use <strong>Preview data</strong> to check the report before saving.
              </div>
              {editPreview?.key === editPreviewKey && (
                <div style={{ background: 'var(--bg-surface-2)', borderRadius: 'var(--radius-md)', padding: '10px 12px', fontSize: 13, marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <PreviewStats preview={editPreview.report} includeAdSense={editAdSense} />
                </div>
              )}
            </div>
            <div>
              <label style={labelStyle}>Dashboard image</label>
              <PageImageField
                value={editAvatarUrl}
                onChange={setEditAvatarUrl}
                onError={(title, message) => notify('error', title, message)}
                placeholder={editTarget.pageType === 'agency' ? 'https://… (agency logo)' : 'https://… (custom image)'}
                helper={
                  <div style={fieldHint(false)}>
                    Shown in the dashboard hero. Upload {editTarget.pageType === 'agency' ? 'the agency’s logo' : 'a custom image'},
                    paste an https:// URL, or clear it to remove the image. Replacing an image we host deletes the old file.
                  </div>
                }
              />
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
              <input type="checkbox" checked={editAdvertisers} onChange={(e) => setEditAdvertisers(e.target.checked)} />
              Show advertisers CG brought to the channels
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
              <input type="checkbox" checked={editAdSense} onChange={(e) => setEditAdSense(e.target.checked)} />
              Show AdSense payout data
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
              <input type="checkbox" checked={editAllowDownloads} onChange={(e) => setEditAllowDownloads(e.target.checked)} />
              Allow downloads (spreadsheet and PDF)
            </label>
            {editTheme && (
              <div>
                <label style={labelStyle}>Theme</label>
                <ThemeEditor
                  theme={editTheme}
                  onChange={(t) => { setEditTheme(t); setEditThemeTouched(true); }}
                  onReset={reExtractTheme}
                  resetLabel={editExtracting ? 'Extracting…' : 'Re-extract from channel art'}
                />
              </div>
            )}
          </div>
        </Modal>
      )}

      {resetTarget && (
        <ResetPinModal
          key={resetTarget.slug}
          eyebrow="Creator dashboards"
          label={resetTarget.clientLabel}
          currentPin={resetTarget.pin}
          onClose={() => setResetTarget(null)}
          onSubmit={runResetPin}
        />
      )}

      <ConfirmDialog
        isOpen={!!deactivateTarget}
        title={`Deactivate ${deactivateTarget?.clientLabel ?? ''}?`}
        message="Visitors will see “deactivated” until you restore it. The link and passcode are kept, and Restore (in the ⋯ menu) brings the dashboard back."
        confirmText="Deactivate"
        variant="danger"
        onConfirm={runDeactivate}
        onCancel={() => setDeactivateTarget(null)}
      />

      {deleteTarget && (
        <Modal
          open
          onClose={() => setDeleteTarget(null)}
          eyebrow="Creator dashboards"
          title={`Delete — ${deleteTarget.clientLabel}`}
          ariaLabel={`Delete ${deleteTarget.clientLabel}`}
          width={500}
          footer={
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Button variant="ghost" onClick={() => setDeleteTarget(null)}>Cancel</Button>
              <Button
                variant="destructive"
                onClick={runDelete}
                disabled={deleting || deleteConfirmText !== deleteTarget.slug}
              >
                {deleting ? 'Deleting…' : 'Delete dashboard forever'}
              </Button>
            </div>
          }
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, fontSize: 13 }}>
            <div style={{ color: 'var(--danger)', fontWeight: 600 }}>
              This permanently deletes the dashboard and its URL — visitors will see “not found” immediately. It cannot be undone.
            </div>
            <div style={{ color: 'var(--fg-3)' }}>
              If you might need it again, use <strong>Deactivate</strong> instead — that keeps the dashboard and lets you restore it later.
            </div>
            <div>
              <label style={labelStyle} htmlFor="cd-delete-confirm">Type the slug to confirm: <code>{deleteTarget.slug}</code></label>
              <input
                id="cd-delete-confirm"
                style={fieldStyle}
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder={deleteTarget.slug}
              />
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default CreatorDashboardsTab;
